import { DiContainer } from '@/modules/di/di-container';
import { IMixingPrograms, ISearchBatchesRequest, ISearchBatchesResponse } from '@/modules/mixing/services/IRecipeMixingService.interface';
import { RecipeMixingService } from '@/modules/mixing/services/recipe-mixing.service';
import { createEffect, createEvent, createStore, sample } from 'effector';
import {
  IExclusiveRecipe,
  IMixingParams,
  IMixingRecipeRoot,
  ITopRecipesResponse,
  RootPricesBuyersItem,
} from './types';
import { IWarehouseApiWithBags } from '@/modules/warehouses/ts';
import { ISearchFilters } from '@/modules/search/store/search.store';
import { IStore } from '@/modules/mixing/components/mixing-by-params-modal/hooks';

const recipeMixingService = DiContainer.get(RecipeMixingService);

export const getWarehouseDetails = createEvent<number>();
export const $warehouseDetails = createStore<any>(null).on(
  getWarehouseDetails,
  (_state, payload) => {
    return payload;
  },
);

export const fetchWarehouseDetails = createEffect(async (id: number): Promise<any> => {
  const data = await recipeMixingService.getWarehouseDetailsById(id);
  return data;
});

sample({
  clock: fetchWarehouseDetails.doneData,
  target: getWarehouseDetails,
});

export const updateMixingData = createEvent<any>();
export const $warehousesForMixingData = createStore<any>([]).on(
  updateMixingData,
  (_, newData) => {
    const modifiedData = modifyData(newData);
    return modifiedData;
  },
);

export const $warehousesForMixingDataResponse = createStore<any>(null).on(
  updateMixingData,
  (_, newData) => {
    return newData;
  },
);

export const fetchWarehousesForMixingDataFx = createEffect(
  async (args: { tons?: number; recipesCount?: number; mixAlgo?: number }) => {
    let data = await recipeMixingService.getWarehousesForMixing(args);
    return data;
  },
);

sample({
  clock: fetchWarehousesForMixingDataFx.doneData,
  target: updateMixingData,
});

export const getMyWarehouses = createEvent();
export const $myWarehouses = createStore<{ name: string; id: number }[] | null>(
  null,
).on(getMyWarehouses, (_, newData) => {
  return newData;
});

export const fetchMyWarehouses = createEffect(async () => {
  try {
    let data = await recipeMixingService.getMyWarehouses();
    return data;
  } catch (err) {
    throw err;
  }
});

sample({
  clock: fetchMyWarehouses.doneData,
  target: getMyWarehouses,
});

export const getWarehouseForCalculator = createEvent();
export const loadMoreSleeves = createEvent();
export const clearCalculatorActiveWarehouseData = createEvent();

export const setLoadingGetSleeves = createEvent<boolean>();
export const setErrorGetSleeves = createEvent();
export const resetLoadingIndicator = createEvent();

export const $calculatorWarehouseByIdLoadingIndicator = createStore({
  loading: false,
  error: false,
})
  .on(setLoadingGetSleeves, (_state, payload) => ({
    loading: payload,
    error: false,
  }))
  .on(setErrorGetSleeves, () => ({
    loading: false,
    error: true,
  }))
  .on(resetLoadingIndicator, () => ({
    loading: false,
    error: false,
  }));

export const $calculatorWarehouseById = createStore(null)
  .on(getWarehouseForCalculator, (_state: any, payload: any) => {
    return payload;
  })
  .on(loadMoreSleeves, (state: any, payload: any) => {
    return { ...state, bags: [...state.bags, ...payload] };
  })
  .on(clearCalculatorActiveWarehouseData, (_state, _payload) => null);

export const fetchWarehouseForCalculatorById = createEffect(
  async ({ id, limit, page }: { id: number; limit?: number; page?: number }) => {
    try {
      let data = await recipeMixingService.getWarehouseForCalculator({
        id,
        limit,
        page,
      });
      return data;
    } catch (err) {
      throw err;
    }
  },
);

export const fetchWarehouseSleevesById = createEffect(
  async ({ id, limit, page }: { id: number; limit: number; page: number }) => {
    try {
      setLoadingGetSleeves(true);
      let data: any = await recipeMixingService.getWarehouseForCalculator({
        id,
        limit,
        page,
      });
      setLoadingGetSleeves(false);
      return data.bags;
    } catch (err) {
      setErrorGetSleeves();
      throw err;
    }
  },
);

// fetchWarehouseSleevesById.pending.watch(() => {
//   setLoadingGetSleeves(true);
// });

// fetchWarehouseSleevesById.done.watch(() => {
//   setLoadingGetSleeves(false);
// });

// fetchWarehouseSleevesById.fail.watch(() => {
//   setLoadingGetSleeves(false);
//   setErrorGetSleeves();
// });

sample({
  clock: fetchWarehouseForCalculatorById.doneData,
  target: getWarehouseForCalculator,
});

sample({
  clock: fetchWarehouseSleevesById.doneData,
  target: loadMoreSleeves,
});

export const updateTopRecipesData = createEvent<any>();
export const $topRecipesLoading = createStore<{
  loading: boolean;
  error: Error | boolean | string;
}>({ loading: false, error: false });
export const $topRecipesForWarehouse = createStore<ITopRecipesResponse | null>(null).on(
  updateTopRecipesData,
  (_, newData) => {
    return newData;
  },
);

const setTopRecipesLoading = createEvent<{
  loading: boolean;
  error: string | boolean | Error;
}>();
$topRecipesLoading.on(setTopRecipesLoading, (_s, p) => p);

export const fetchTopRecipesForWarehouse = createEffect(async (id: number) => {
  try {
    setTopRecipesLoading({ loading: true, error: false });
    let data = await recipeMixingService.getTopRecipes(id);
    setTopRecipesLoading({ loading: false, error: false });
    return data;
  } catch (err: any) {
    setTopRecipesLoading({ loading: false, error: err });
    throw err;
  }
});

sample({
  clock: fetchTopRecipesForWarehouse.doneData,
  target: updateTopRecipesData,
});

// fetchWarehousesForMixingDataFx.done.watch((result) => {
//   const modifiedData = modifyData(result);
//   updateMixingData(modifiedData);
// });

export const updateAvailableBunkers = createEvent<any>();
export const $availableBunkers = createStore<any>(null).on(
  updateAvailableBunkers,
  (_, newData) => {
    return newData;
  },
);

export const fetchAvailableBunkers = createEffect(async () => {
  let data = await recipeMixingService.getAvailableBunkers();
  return data;
});

sample({
  clock: fetchAvailableBunkers.doneData,
  target: updateAvailableBunkers,
});

export const getRecipeData = createEvent<any>();
export const cleanRecipeData = createEvent<any>();
export const setRecipeInfoLoadingStatus = createEvent();
export const $recipeInfo = createStore<IMixingRecipeRoot | null>(null)
  .on(getRecipeData, (_, newData) => {
    return newData;
  })
  .on(cleanRecipeData, (_, newData) => {
    return null;
  });
export const $recipeInfoLoadingStatus = createStore<{
  loading: boolean;
  error: boolean;
}>({ loading: false, error: false }).on(setRecipeInfoLoadingStatus, (_s, p) => p);

export const fetchRecipeData = createEffect(
  async ({ id, bunkers }: { id: number; bunkers: any }) => {
    let data = await recipeMixingService.fetchRecipeData(id, bunkers);
    return data;
  },
);

sample({
  clock: fetchRecipeData.doneData,
  target: getRecipeData,
});

sample({
  clock: fetchRecipeData.failData,
  target: setRecipeInfoLoadingStatus,
  fn: (err) => ({ error: err.message, loading: false }),
});

sample({
  clock: fetchRecipeData.pending,
  target: setRecipeInfoLoadingStatus,
  fn: (err) => ({ error: false, loading: true }),
});

export const getWarehouseData = createEvent();
export const clearWrehouseData = createEvent();
export const $warehouseData = createStore<IWarehouseApiWithBags | null>(null)
  .on(getWarehouseData, (_, newData) => {
    return newData;
  })
  .on(clearWrehouseData, () => {
    return null;
  });

export const fetchWarehouseData = createEffect(async (id: number) => {
  let data = await recipeMixingService.getWarehouseById(id);
  return data;
});

sample({
  clock: fetchWarehouseData.doneData,
  target: getWarehouseData,
});

export const getMixingParams = createEvent();
export const $mixingParamsData = createStore<IMixingParams | null>(null).on(
  getMixingParams,
  (_, newData) => {
    return newData;
  },
);

export const setMixingParamsError = createEvent<any>();
export const $mixingParamsError = createStore<any>(null).on(setMixingParamsError, (_s, p) => p);

export const fetchMixingParamsData = createEffect(async (body: IMixingPrograms) => {
  try {
    setMixingParamsError(null);
    let data: any = await recipeMixingService.getMixingParams(body);
    if (data.error) setMixingParamsError(data.error);
    return data;
  } catch (err) {
    setMixingParamsError(err);
    return err;
  }
});


export const $searchBatches = createStore<ISearchBatchesResponse | null>(null);
export const $searchBatchesStatus = createStore({ error: false, loading: false });
export const setSearchBatches = createEvent<any>();
export const setSearchBatchesStatus = createEvent<{ error: any, loading: boolean }>();
$searchBatchesStatus.on(setSearchBatchesStatus, (_s, p) => p);
$searchBatches.on(setSearchBatches, (_S, p) => p);
export const fetchSearchBatches = createEffect(async (body: ISearchBatchesRequest)=> {
  try {
    setSearchBatchesStatus({ error: false, loading: true });
    const data: any = await recipeMixingService.getSearchBatches(body);
    if ('errors' in data || data instanceof Error) {
      throw data;
    }
    setSearchBatchesStatus({ error: false, loading: false });
    return data;
  } catch (err: any) {
    setSearchBatchesStatus({ error: err.message, loading: false } as any)
    return err;
  }
});

sample({
  target: setSearchBatches, 
  clock: fetchSearchBatches.doneData,
})



sample({
  clock: fetchMixingParamsData.doneData,
  target: getMixingParams,
});


export const getBuyersPrices = createEvent();
const setBuyersPricesDataLoading = createEvent<{
  loading: boolean;
  error: Error | boolean;
}>();

export const $buyersPricesDataLoading = createStore<{
  loading: boolean;
  error: Error | boolean;
}>({ loading: false, error: false }).on(setBuyersPricesDataLoading, (_s, p) => p);
// @ts-ignore
export const $buyersPricesData = createStore<RootPricesBuyersItem[] | null>(null).on(
  getBuyersPrices,
  (_, newData: any) => {
    const res = Object.values(newData);
    return res;
  },
);

export const fetchBuyersPricesData = createEffect(
  async ({ filters }: { filters: ISearchFilters }) => {
    try {
      setBuyersPricesDataLoading({ loading: true, error: false });
      let data = await recipeMixingService.getBuyersPrices(filters);
      setBuyersPricesDataLoading({ loading: false, error: false });
      return data;
    } catch (error) {
      setBuyersPricesDataLoading({ loading: false, error: error as Error });
      throw error;
    }
  },
);

sample({
  clock: fetchBuyersPricesData.doneData,
  target: getBuyersPrices,
});

function modifyData(data: any) {
  // console.log(data, ' modifyData')
  const modifiedData = data?.data?.map((item: any) => ({
    title: item.name,
    id: item.id,
    type: item.type,
    actions: item.id,
    show: item.show,
    moreDetails: item.more_details,
    weight: item.weight,
    resSumMix: item.res_sum_mix,
    data: [
      {
        label: 'выгода от миксования',
        marker: true,
        value:
          item.res_sum_mix > 0
            ? `+ ${item.res_sum_mix?.toLocaleString('RU-ru')} ₽`
            : item.res_sum_mix,
      },
      {
        label: 'общий вес пшеницы на складе',
        value: `${item.weight} т`,
      },
      {
        label: 'стоимость склада по рыночной цене',
        value: `${item?.market?.all?.toLocaleString('RU-ru')} ₽`,
        postfix: `${item?.market?.one?.toLocaleString('RU-ru')} ₽/т`,
      },
      {
        label: 'стоимость склада (нетто) включая логистику',
        value: `0 ₽`, // ${item?.exchange?.all?.toLocaleString('RU-ru')}
        postfix: `${item?.exchange?.one?.toLocaleString('RU-ru')} ₽/т`,
      },
      {
        label: 'стоимость склада при миксовании',
        value: `${item?.using_mixing?.all?.toLocaleString('RU-ru')} ₽`,
        postfix: `${item?.using_mixing?.one?.toLocaleString('RU-ru')} ₽/т`,
      },
    ],
  }));
  return modifiedData;
}
